import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './RestaurantApply.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import MapSelector from '../../components/MapSelector/MapSelector';
import { validateLocation } from '../../utils/locationUtils';
import { getEndpointUrl } from '../../utils/apiConfig';

const RestaurantApply = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [locationValidation, setLocationValidation] = useState({ isValid: true, message: '' });
  const [formData, setFormData] = useState({
    restaurantName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      country: '',
      zipCode: '',
      quartier: '',
      coordinates: {
        latitude: null,
        longitude: null
      }
    },
    cuisineType: '',
    description: '',
    website: '',
    hasDeliveryService: false,
    documents: {
      businessLicense: null,
      businessLicenseExpiry: '',
      menuLicense: null,
      menuLicenseExpiry: '',
      insuranceCertificate: null,
      insuranceCertificateExpiry: ''
    },
    additionalInfo: '',
    mapCoordinates: {
      latitude: 0,
      longitude: 0
    }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prevData => ({
        ...prevData,
        address: {
          ...prevData.address,
          [addressField]: value
        }
      }));
    } else if (type === 'file' && name.startsWith('documents.')) {
      const documentType = name.split('.')[1];
      const file = files[0]; // Single file upload
      
      if (file) {
        // Validate file size (max 5MB per file)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        
        if (file.size > maxSize) {
          toast.error(t('restaurantApply.file_too_large', { size: (file.size / 1024 / 1024).toFixed(2) }), {
            position: "top-right",
            autoClose: 5000
          });
          // Clear the input
          e.target.value = '';
          return;
        }
        
        setFormData(prevData => ({
          ...prevData,
          documents: {
            ...prevData.documents,
            [documentType]: file
          }
        }));
      } else {
        // File was cleared
        setFormData(prevData => ({
          ...prevData,
          documents: {
            ...prevData.documents,
            [documentType]: null
          }
        }));
      }
    } else if (name.startsWith('documents.') && name.endsWith('Expiry')) {
      const docType = name.split('.')[1];
      setFormData(prevData => ({
        ...prevData,
        documents: {
          ...prevData.documents,
          [docType]: value
        }
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleLocationSelect = (location) => {
    // Validate the selected location
    const validation = validateLocation(location.lat, location.lng);
    setLocationValidation(validation);
    
    setFormData(prevData => ({
      ...prevData,
      address: {
        ...prevData.address,
        coordinates: {
          latitude: location.lat,
          longitude: location.lng
        }
      }
    }));
    
    // Show temporary message if location is outside service area
    if (!validation.isValid) {
      setTimeout(() => {
        setLocationValidation(prev => ({ ...prev, message: '' }));
      }, 8000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that location coordinates are provided (REQUIRED)
    const { latitude, longitude } = formData.address.coordinates;
    if (!latitude || !longitude || latitude === null || longitude === null) {
      toast.error(t('restaurantApply.location_required'), {
        position: "top-right",
        autoClose: 5000
      });
      // Auto-open the map if it's not already open
      setShowMap(true);
      // Scroll to map section
      const mapSection = document.querySelector('.map-section');
      if (mapSection) {
        mapSection.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }
    
    // Validate that the location is within service area
    const validation = validateLocation(latitude, longitude);
    if (!validation.isValid) {
      toast.error(t('restaurantApply.location_outside_service_area', { message: validation.message }), {
        position: "top-right",
        autoClose: 6000
      });
      return;
    }
    
    // Validate that all 3 documents are uploaded
    const { businessLicense, businessLicenseExpiry, menuLicense, menuLicenseExpiry, insuranceCertificate, insuranceCertificateExpiry } = formData.documents;
    if (!businessLicense || !menuLicense || !insuranceCertificate || !businessLicenseExpiry || !menuLicenseExpiry || !insuranceCertificateExpiry) {
      const missingDocs = [];
      if (!businessLicense) missingDocs.push(t('restaurantApply.business_license'));
      if (!businessLicenseExpiry) missingDocs.push(t('restaurantApply.business_license_expiry'));
      if (!menuLicense) missingDocs.push(t('restaurantApply.menu_food_license'));
      if (!menuLicenseExpiry) missingDocs.push(t('restaurantApply.menu_food_license_expiry'));
      if (!insuranceCertificate) missingDocs.push(t('restaurantApply.insurance_certificate'));
      if (!insuranceCertificateExpiry) missingDocs.push(t('restaurantApply.insurance_certificate_expiry'));
      
      toast.error(t('restaurantApply.missing_documents', { docs: missingDocs.join(', ') }));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Create FormData object to handle file uploads
      const applicationData = new FormData();
      
      // Add all form fields to FormData
      for (const key in formData) {
        if (key === 'address') {
          // Append nested address fields, handling coordinates specially
          for (const addressKey in formData.address) {
            if (addressKey === 'coordinates' && formData.address[addressKey]) {
              // Handle coordinates as nested object
              const coords = formData.address[addressKey];
              if (coords.latitude && coords.longitude && coords.latitude !== null && coords.longitude !== null) {
                applicationData.append(`address[coordinates][latitude]`, coords.latitude);
                applicationData.append(`address[coordinates][longitude]`, coords.longitude);
              }
            } else if (formData.address[addressKey] && formData.address[addressKey] !== '') {
            applicationData.append(`address[${addressKey}]`, formData.address[addressKey]);
            }
          }
        } else if (key === 'documents' && formData[key]) {
          // Handle individual document files
          const { businessLicense, businessLicenseExpiry, menuLicense, menuLicenseExpiry, insuranceCertificate, insuranceCertificateExpiry } = formData[key];
          if (businessLicense) applicationData.append('documents', businessLicense);
          if (businessLicenseExpiry) applicationData.append('documentsExpiry[]', businessLicenseExpiry);
          if (menuLicense) applicationData.append('documents', menuLicense);
          if (menuLicenseExpiry) applicationData.append('documentsExpiry[]', menuLicenseExpiry);
          if (insuranceCertificate) applicationData.append('documents', insuranceCertificate);
          if (insuranceCertificateExpiry) applicationData.append('documentsExpiry[]', insuranceCertificateExpiry);
        } else if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
          applicationData.append(key, formData[key]);
        }
      }
      
      // Log submission for debugging
      console.log('Submitting restaurant application with form data:', formData);
      console.log('Submitting restaurant application with FormData entries:', Object.fromEntries(applicationData.entries()));
      
      // Use the main applications endpoint that processes expiry dates (same as driver flow)
      const apiUrl = getEndpointUrl('/api/application/restaurant');
      console.log('Using API URL:', apiUrl);
      
      const response = await axios.post(
        apiUrl, 
        applicationData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 15000, // Increased timeout for file uploads
          withCredentials: false
        }
      );
      
      console.log('Restaurant application submission response:', response.data);
      
      // Show success message
      toast.success(t('restaurantApply.application_submitted_successfully'), {
        position: "top-right",
        autoClose: 5000
      });
      
      // Reset form can be simpler now
      setFormData({
        restaurantName: '',
        ownerName: '',
        email: '',
        phone: '',
        address: { 
          street: '', 
          city: '', 
          country: '', 
          zipCode: '', 
          quartier: '',
          coordinates: {
            latitude: null,
            longitude: null
          }
        },
        cuisineType: '',
        description: '',
        website: '',
        hasDeliveryService: false,
        documents: {
          businessLicense: null,
          businessLicenseExpiry: '',
          menuLicense: null,
          menuLicenseExpiry: '',
          insuranceCertificate: null,
          insuranceCertificateExpiry: ''
        },
        additionalInfo: '',
        mapCoordinates: {
          latitude: 0,
          longitude: 0
        }
      });
      
      // Navigate back to home page after short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(t('restaurantApply.submission_problem'), {
        position: "top-right",
        autoClose: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="restaurant-apply-container">
      <div className="apply-header">
        <h1>{t('restaurantApply.become_partner')}</h1>
        <p>{t('restaurantApply.join_network_description')}</p>
      </div>

      <form className="restaurant-apply-form" onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>{t('restaurantApply.restaurant_information')}</h2>
          
          <div className="form-group">
            <label htmlFor="restaurantName">{t('restaurantApply.restaurant_name')}</label>
            <input
              type="text"
              id="restaurantName"
              name="restaurantName"
              value={formData.restaurantName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="cuisineType">{t('restaurantApply.cuisine_type')}</label>
            <input
              type="text"
              id="cuisineType"
              name="cuisineType"
              value={formData.cuisineType}
              onChange={handleChange}
              required
              placeholder={t('restaurantApply.cuisine_type_placeholder')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('restaurantApply.restaurant_description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder={t('restaurantApply.description_placeholder')}
              rows="4"
            ></textarea>
          </div>

          <div className="form-group">
            <label htmlFor="website">{t('restaurantApply.website_optional')}</label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              placeholder={t('restaurantApply.website_placeholder')}
            />
          </div>

          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="hasDeliveryService"
              name="hasDeliveryService"
              checked={formData.hasDeliveryService}
              onChange={handleChange}
            />
            <label htmlFor="hasDeliveryService">{t('restaurantApply.has_delivery_service')}</label>
          </div>
        </div>

        <div className="form-section">
          <h2>{t('restaurantApply.contact_information')}</h2>
          
          <div className="form-group">
            <label htmlFor="ownerName">{t('restaurantApply.owner_manager_name')}</label>
            <input
              type="text"
              id="ownerName"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('restaurantApply.email_address')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('restaurantApply.phone_number')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              pattern="(\+257)?[0-9]{8}"
              placeholder={t('restaurantApply.phone_placeholder')}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>{t('restaurantApply.restaurant_location')}</h2>
          
          <div className="form-group">
            <label htmlFor="address.street">{t('restaurantApply.address_street')}</label>
            <input
              type="text"
              id="address.street"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.quartier">{t('restaurantApply.quartier')}</label>
            <input
              type="text"
              id="address.quartier"
              name="address.quartier"
              value={formData.address.quartier}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address.city">{t('restaurantApply.city')}</label>
            <input
              type="text"
              id="address.city"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="address.country">{t('restaurantApply.country')}</label>
              <input
                type="text"
                id="address.country"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="address.zipCode">{t('restaurantApply.zip_code')}</label>
              <input
                type="text"
                id="address.zipCode"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Map Location Section */}
          <div className="map-section">
            <label>{t('restaurantApply.location_on_map')}</label>
            <p className="help-text required-field">
              <strong>{t('restaurantApply.required')}:</strong> {t('restaurantApply.pin_location_description')}
            </p>
            
            <button 
              type="button" 
              className="toggle-map-btn" 
              onClick={() => setShowMap(!showMap)}
            >
              {showMap ? t('restaurantApply.hide_map') : t('restaurantApply.select_location_on_map')}
            </button>
            
            {/* Location Status Indicator */}
            <div className={`location-status ${formData.address.coordinates.latitude && formData.address.coordinates.longitude ? 'has-location' : 'no-location'}`}>
              {formData.address.coordinates.latitude && formData.address.coordinates.longitude ? (
                <span className="status-text success">
                  {t('restaurantApply.location_selected')}
                </span>
              ) : (
                <span className="status-text warning">
                  {t('restaurantApply.please_select_location')}
                </span>
              )}
            </div>
            
            {/* Location Validation Message */}
            {locationValidation.message && (
              <div className={`location-validation-message ${!locationValidation.isValid ? 'warning' : 'success'}`}>
                <span className="validation-icon">
                  {locationValidation.isValid ? '✅' : '⚠️'}
                </span>
                <span>{locationValidation.message}</span>
              </div>
            )}
            
            {showMap && (
              <>
                <MapSelector 
                  onLocationSelect={handleLocationSelect}
                  initialPosition={
                    formData.address.coordinates.latitude && formData.address.coordinates.longitude ? 
                    { 
                      lat: formData.address.coordinates.latitude, 
                      lng: formData.address.coordinates.longitude 
                    } : 
                    null
                  }
                />
                {formData.address.coordinates.latitude && formData.address.coordinates.longitude && (
                  <div className={`location-confirmation ${!locationValidation.isValid ? 'invalid' : 'valid'}`}>
                    <p>
                      {locationValidation.isValid ? '✅' : '⚠️'} Location marked at coordinates: {formData.address.coordinates.latitude.toFixed(6)}, {formData.address.coordinates.longitude.toFixed(6)}
                      {!locationValidation.isValid && (
                        <span className="location-note"> - Outside service area</span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="form-section">
          <h2>{t('restaurantApply.required_documents')}</h2>
          
          <div className="documents-section">
            <small className="file-requirements">
              <strong>{t('restaurantApply.all_documents_required')}:</strong><br/>
              <em>{t('restaurantApply.file_requirements')}</em>
            </small>

            <div className="document-upload-grid">
              <div className="form-group document-upload">
                <label htmlFor="businessLicense">{t('restaurantApply.business_license')}</label>
                <input
                  type="file"
                  id="businessLicense"
                  name="documents.businessLicense"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.businessLicense && (
                  <div className="file-selected">
                    ✅ {formData.documents.businessLicense.name} ({(formData.documents.businessLicense.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="businessLicenseExpiry">{t('restaurantApply.expiration_date')}</label>
                <input
                  type="date"
                  id="businessLicenseExpiry"
                  name="documents.businessLicenseExpiry"
                  value={formData.documents.businessLicenseExpiry}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group document-upload">
                <label htmlFor="menuLicense">{t('restaurantApply.menu_food_license')}</label>
                <input
                  type="file"
                  id="menuLicense"
                  name="documents.menuLicense"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.menuLicense && (
                  <div className="file-selected">
                    ✅ {formData.documents.menuLicense.name} ({(formData.documents.menuLicense.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="menuLicenseExpiry">{t('restaurantApply.expiration_date')}</label>
                <input
                  type="date"
                  id="menuLicenseExpiry"
                  name="documents.menuLicenseExpiry"
                  value={formData.documents.menuLicenseExpiry}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group document-upload">
                <label htmlFor="insuranceCertificate">{t('restaurantApply.insurance_certificate')}</label>
                <input
                  type="file"
                  id="insuranceCertificate"
                  name="documents.insuranceCertificate"
                  onChange={handleChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  required
                />
                {formData.documents.insuranceCertificate && (
                  <div className="file-selected">
                    ✅ {formData.documents.insuranceCertificate.name} ({(formData.documents.insuranceCertificate.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
              </div>

              <div className="form-group document-upload">
                <label htmlFor="insuranceCertificateExpiry">{t('restaurantApply.expiration_date')}</label>
                <input
                  type="date"
                  id="insuranceCertificateExpiry"
                  name="documents.insuranceCertificateExpiry"
                  value={formData.documents.insuranceCertificateExpiry}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="documents-status">
              {(() => {
                const uploadedCount = Object.values(formData.documents).filter(doc => doc !== null).length;
                if (uploadedCount === 6) {
                  return <div className="file-success">{t('restaurantApply.all_documents_uploaded', { count: uploadedCount })}</div>;
                } else {
                  return <div className="file-warning">{t('restaurantApply.upload_all_documents', { count: uploadedCount })}</div>;
                }
              })()}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="additionalInfo">{t('restaurantApply.additional_information')}</label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              placeholder={t('restaurantApply.additional_info_placeholder')}
              rows="3"
            ></textarea>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="submit" 
            className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('restaurantApply.submitting') : t('restaurantApply.submit_application')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RestaurantApply; 